import React, { useMemo, useState } from "react";
import { Modal, Form, Input, Button, Switch, message } from "antd";
import OrderManager from "@/pages/Admin/OrdersManagement/component/OrderManager";
import CreateEventOrderModal from "./modals/CreateEventOrder";
import EventOrderDetailsModal from "./modals/EventOrderDetails";
import BulkFulfillmentModal from "@/pages/Admin/OrdersManagement/component/BulkFulfillmentModal";
import { useAddDriverMutation } from "@/services/api/driver/driverApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import { useCateringOrderData } from "./hooks/useCateringOrderData";
import { formatCurrency, formatCustomerName, formatDate } from "@/utils/helper";
import BulkOrderDetailsModal from "../BulkOrders/modals/BulkOrderDetails";
import BookedEventDetailsModal from "./modals/BookedEventDetails";
import OrderFilterDrawer from "../component/OrderFilterDrawer";

const CateringEvent = () => {
  const {
    cateringOrdersData,
    summary,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    filters,
    handleFilterChange,
    handleClearFilters,
    hasActiveFilters,
    isLoading: cateringLoading,
    isFetching,
  } = useCateringOrderData();

  const [addDriver, { isLoading: addingDriver }] = useAddDriverMutation();
  const { data: locations = [], isLoading: locationLoading } =
    useGetLocationsQuery();
  const [driverForm] = Form.useForm();

  // Modal states
  const [eventOrderOpen, setEventOrderOpen] = useState(false);
  const [openCateringPackageOrderDetails, setOpenCateringPackageOrderDetails] =
    useState(false);
  const [openBookedEventDetails, setOpenBookedEventDetails] = useState(false);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [bulkFulfillmentOpen, setBulkFulfillmentOpen] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Location options for filter
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: loc.id,
        label: `${loc.name} - ${loc.state?.name || ""}`,
      })),
    [locations]
  );

  // Map API data to table format
  const mappedCateringOrdersData = useMemo(() => {
    if (!cateringOrdersData || cateringOrdersData.length === 0) return [];

    return cateringOrdersData.map((order) => ({
      key: order.id.toString(),
      orderId: order.reference,
      customer: formatCustomerName(order.user),
      orderDate: formatDate(order.created_at),
      branch: order.location?.name || "Unknown Location",
      orderDueDate: formatDate(order.delivery_date),
      amount: formatCurrency(order.amount),
      items: order.items_count || 0,
      status: order.status?.name || "Unknown",
      orderType: order.order_type_id,
      originalData: order,
    }));
  }, [cateringOrdersData]);

  // Map API summary to metrics
  const orderMetrics = useMemo(() => {
    return [
      {
        title: "Total Orders",
        value: summary?.total_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Pending Orders",
        value: summary?.total_pending_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Completed Orders",
        value: summary?.total_completed_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Avg Processing Time",
        value: ` ${summary?.average_processing_time} Days `,
        change: "0",
        trend: "none",
      },
    ];
  }, [summary]);

  const openCreateModal = () => {
    setEventOrderOpen(true);
  };

  const openDetailsModal = (orderData) => {
    setSelectedOrder(orderData);
    setOpenOrderDetails(true);
  };

  const openCateringPackageOrderDetailsModal = (orderData) => {
    setSelectedOrder(orderData);
    setOpenCateringPackageOrderDetails(true);
  };

  const closeCateringPackageOrderModal = () => {
    setOpenCateringPackageOrderDetails(false);
    setSelectedOrder(null);
  };

  const openBookedEventModal = (orderData) => {
    setSelectedOrder(orderData);
    setOpenBookedEventDetails(true);
  };

  const closeBookedEventModal = () => {
    setOpenBookedEventDetails(false);
    setSelectedOrder(null);
  };

  const closeOrderDetailsModal = () => {
    setOpenOrderDetails(false);
    setSelectedOrder(null);
  };

  const handleBulkFulfillment = (stage, orderKeys) => {
    // Extract original order IDs from the selected keys
    const orderIds = orderKeys
      .map((key) => {
        const order = mappedCateringOrdersData.find((o) => o.key === key);
        return order?.originalData?.id;
      })
      .filter(Boolean);

    setCurrentStage(stage);
    setSelectedOrderIds(orderIds);
    setBulkFulfillmentOpen(true);
  };

  const closeBulkFulfillmentModal = () => {
    setBulkFulfillmentOpen(false);
    setCurrentStage(null);
    setSelectedOrderIds([]);
  };

  const handleAddDriver = async (values) => {
    try {
      await addDriver(values).unwrap();
      message.success("Driver added successfully!");
      setShowDriverDialog(false);
      driverForm.resetFields();
    } catch (error) {
      message.error("Failed to add driver. Please try again.");
    }
  };

  return (
    <div>
      <OrderManager
        tableData={mappedCateringOrdersData}
        showBookedEvent={true}
        metricData={orderMetrics}
        buttonText="Create Event Order"
        tableTitle="All Catering Orders"
        onCreate={openCreateModal}
        openDetailsModal={openDetailsModal}
        openCateringPackageOrderDetailsModal={
          openCateringPackageOrderDetailsModal
        }
        openBookedEventModal={openBookedEventModal}
        onBulkFulfillment={handleBulkFulfillment}
        isLoading={cateringLoading || isFetching}
        paginationData={paginationInfo}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        search={search}
        handleSearch={handleSearch}
        searchMode="server"
        onFilterClick={() => setFilterDrawerOpen(true)}
        hasActiveFilters={hasActiveFilters}
      />

      <CreateEventOrderModal
        open={eventOrderOpen}
        onClose={() => setEventOrderOpen(false)}
      />

      <EventOrderDetailsModal
        open={openOrderDetails}
        onClose={closeOrderDetailsModal}
        orderData={selectedOrder}
      />

      <BulkOrderDetailsModal
        open={openCateringPackageOrderDetails}
        onClose={closeCateringPackageOrderModal}
        orderData={selectedOrder}
      />

      <BookedEventDetailsModal
        open={openBookedEventDetails}
        onClose={closeBookedEventModal}
        orderData={selectedOrder}
      />

      <BulkFulfillmentModal
        open={bulkFulfillmentOpen}
        onClose={closeBulkFulfillmentModal}
        selectedOrderIds={selectedOrderIds}
        currentStage={currentStage}
        onShowDriverDialog={() => setShowDriverDialog(true)}
      />

      <OrderFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        locationOptions={locationOptions}
        locationLoading={locationLoading}
      />

      {/* Add Driver Modal */}
      <Modal
        open={showDriverDialog}
        onCancel={() => {
          setShowDriverDialog(false);
          driverForm.resetFields();
        }}
        title="Add New Driver"
        footer={null}
        destroyOnClose
      >
        <Form form={driverForm} layout="vertical" onFinish={handleAddDriver}>
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="John" />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: "Please enter last name" }]}
          >
            <Input placeholder="Doe" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^\+?[0-9]{11,15}$/,
                message: "Enter valid phone number",
              },
            ]}
          >
            <Input placeholder="+2348012345678" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Enter valid email" },
            ]}
          >
            <Input placeholder="johndoe@example.com" />
          </Form.Item>
          <Form.Item
            name="location_id"
            label="Location ID"
            rules={[{ required: true, message: "Please enter location ID" }]}
          >
            <Input type="number" placeholder="2" />
          </Form.Item>
          <Form.Item
            name="license_number"
            label="License Number"
            rules={[{ required: true, message: "Please enter license number" }]}
          >
            <Input placeholder="LIC-123456" />
          </Form.Item>
          <Form.Item name="status" valuePropName="checked" initialValue={true}>
            <div className="flex items-center gap-2">
              <Switch defaultChecked />
              <span>Active Status</span>
            </div>
          </Form.Item>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                setShowDriverDialog(false);
                driverForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={addingDriver}>
              Add Driver
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default CateringEvent;
